/**
 * How the merchant panel behaves on phones and tablets — the one file to change.
 *
 * Nothing here alters the desktop look: every rule applies only below a breakpoint. The pieces:
 *   - the two breakpoints, and the hook the page frame uses to ask "is this a phone or tablet?"
 *   - phone-only fixes for shared MUI components (dialogs, tab bars, popups), merged into the theme
 *   - PAGE_RULES: extra phone-only rules for one page, found by its route, for screens that need more
 *     than the shared rules give them (no page file needs editing)
 */
import { useEffect, useMemo } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

/** Below this width the sidebar becomes a slide-over menu and pages use the full width. */
export const FRAME_BREAKPOINT = 'md';

/** Below this width the compact component rules below apply. */
export const PHONE_BREAKPOINT = 'sm';

/** Left offset of the page area on desktop: the sidebar (248 open, 60 icon rail) plus an 8px gap. */
export const desktopContentMargin = (iconRail) => (iconRail ? 68 : 256);

/** True on phones and tablets in portrait, where the sidebar is a slide-over menu. */
export function useIsMobileFrame() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down(FRAME_BREAKPOINT));
}

/**
 * Extra rules for one page on phones, keyed by the page's route path. Each value is a style object
 * whose selectors are relative to that page, e.g. { '/orders': { '.some-class': { minWidth: 0 } } }.
 * Start empty; add a page only after seeing it on a phone.
 */
export const PAGE_RULES = {};

/**
 * Phone-only rules for filter forms. Many screens lay their filters out as fractions of a row
 * (`xs={2}`, `xs={3}`) or as boxes with a fixed `minWidth`; on a phone that squeezes each field to a
 * sliver with a cut-off label. Here every field takes the full row instead, one per line.
 */
const PHONE_FORM_FIELD_RULES = {
  '.MuiGrid-container > .MuiGrid-item:has(.MuiInputBase-root, .MuiAutocomplete-root)': {
    flexBasis: '100% !important', maxWidth: '100% !important', width: '100% !important',
  },
  ':is(.MuiBox-root, .MuiStack-root):has(> .MuiFormControl-root, > .MuiTextField-root)': {
    minWidth: '0 !important', width: '100% !important', maxWidth: '100% !important', flexWrap: 'wrap',
  },
  // A row of controls (search box, date picker, buttons) wraps; a field in it keeps a usable width
  // instead of being squeezed to a few characters.
  ':is(.MuiBox-root, .MuiStack-root) > :is(.MuiFormControl-root, .MuiTextField-root)': {
    minWidth: '140px', flex: '1 1 140px',
  },
  // A row of buttons wraps onto a new line instead of squeezing each button until its label spills out.
  ':is(.MuiBox-root, .MuiStack-root):has(> .MuiButton-root)': { flexWrap: 'wrap' },
  // A table wider than the screen scrolls sideways inside its own box (some are set to hide it), and
  // its columns keep a readable width instead of being crushed (AWB numbers broke one character
  // per line). Checkbox columns stay narrow.
  '.MuiTableContainer-root': { overflowX: 'auto !important' },
  '.MuiTableContainer-root .MuiTable-root': { tableLayout: 'auto !important' }, // fixed layout ignores column minimums
  '.MuiTableContainer-root :is(.MuiTableCell-head, .MuiTableCell-body):not(.MuiTableCell-paddingCheckbox)': {
    minWidth: '96px',
  },
  // iPhones zoom the page when a field with text under 16px is focused; 16px stops that.
  'input:not([type=checkbox]):not([type=radio]):not([type=range]), textarea, select': {
    fontSize: '16px !important',
  },
  // ...but keep the small fields the same height as before (larger text would make them taller than
  // the dropdowns next to them).
  'input.MuiInputBase-inputSizeSmall': {
    height: 'auto !important', paddingTop: '8px !important', paddingBottom: '8px !important',
  },
  // A tall vertical stepper (330px of steps) becomes one compact row on a phone: every step as a
  // numbered circle, with the label shown only for the current step.
  '.MuiStepper-vertical': { flexDirection: 'row !important', flexWrap: 'nowrap', alignItems: 'center', columnGap: '6px' },
  '.MuiStepper-vertical .MuiStepLabel-iconContainer': { paddingRight: '6px' },
  '.MuiStepper-vertical .MuiStepLabel-label': { fontSize: '0.8rem' },
  '.MuiStepper-vertical .MuiStepConnector-root': { display: 'none' },
  '.MuiStepper-vertical .MuiStepLabel-label:not(.Mui-active)': { display: 'none' },
  '.MuiStepper-vertical .MuiStep-root': { padding: 0 },
  // A faint shadow on a data grid's right edge: there are more columns to swipe to.
  '.MuiDataGrid-main': { position: 'relative' },
  '.MuiDataGrid-main::after': {
    content: '""', position: 'absolute', top: 0, right: 0, bottom: 0, width: '18px', pointerEvents: 'none', zIndex: 3,
    background: 'linear-gradient(to left, rgba(0, 0, 0, 0.07), rgba(0, 0, 0, 0))',
  },
  // Rows the fit-to-screen step made scrollable (e.g. status tab strips): a thin bar shows they scroll.
  '[data-rp-fit="scroll"]': { scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' },
};

/**
 * Phone-only "Filters" bar. A filter panel marked with `data-rp-filters` shows as one tappable
 * "Filters ▾" bar and opens on tap, so a list's data isn't pushed a screen and a half down by nine
 * open fields. The fields stay mounted (nothing is lost); applying a filter closes it again. The tap
 * handling is `usePhoneFilterToggle`.
 */
const phoneFilterPanelRules = (theme) => ({
  '[data-rp-filters]::before': {
    content: '"Filters  \\25BE"', display: 'block', flexBasis: '100%', cursor: 'pointer',
    fontWeight: 600, fontSize: '14px', padding: '10px 4px', color: theme.palette.text.primary,
  },
  '[data-rp-filters][data-open="true"]::before': { content: '"Filters  \\25B4"', marginBottom: '8px' },
  '[data-rp-filters]:not([data-open="true"]) > *': { display: 'none !important' },
  '[data-rp-filters]:not([data-open="true"])': { paddingTop: '0 !important', paddingBottom: '0 !important' },
});

/**
 * Phone-only rules for the date-range picker (react-date-range) used all over the panel. On desktop
 * it is a 890px-wide panel (shortcut list + two months side by side); on a phone that is cut off at
 * the right edge and the calendar cannot be used. Here it stacks: shortcuts above, one month below.
 * `!important` because the library's own stylesheet loads after the theme's.
 */
const DATE_RANGE_PICKER_PHONE_RULES = {
  '.MuiPopover-paper:has(.rdrDateRangePickerWrapper)': { backgroundColor: '#fff !important' },
  '.rdrDateRangePickerWrapper': { flexDirection: 'column !important', alignItems: 'stretch !important', width: '100% !important' },
  '.rdrDefinedRangesWrapper': { width: '100% !important', borderRight: '0 !important', borderBottom: '1px solid #eff2f7 !important' },
  '.rdrStaticRanges': { flexDirection: 'row !important', flexWrap: 'wrap !important' },
  '.rdrStaticRange': { flex: '1 1 33% !important', borderBottom: '0 !important' },
  '.rdrCalendarWrapper': { width: '100% !important', maxWidth: '100% !important' },
  '.rdrMonths.rdrMonthsHorizontal': { flexDirection: 'column !important' },
  '.rdrMonth': { width: '100% !important' },
  '.rdrMonths .rdrMonth + .rdrMonth': { display: 'none !important' },
};

/** Global rules for phones: the shared rules above, plus PAGE_RULES. Merged into the theme's CssBaseline. */
export function responsiveGlobalStyles(theme) {
  const pages = Object.entries(PAGE_RULES);
  return {
    [theme.breakpoints.down(PHONE_BREAKPOINT)]: {
      ...DATE_RANGE_PICKER_PHONE_RULES,
      ...PHONE_FORM_FIELD_RULES,
      ...phoneFilterPanelRules(theme),
      ...Object.fromEntries(pages.map(([route, rules]) => [`[data-route="${route}"]`, rules])),
    },
  };
}

/**
 * Safety net for phones and tablets: finds what actually runs off the screen and fits it.
 *   - a row of items set not to wrap, whose items overflow it, is allowed to wrap
 *   - any element wider than the page area is capped at the width of the screen
 * Rows that already fit are never touched, so nothing that works changes. Tables, grids and anything
 * already inside a sideways-scrolling container are left alone. Re-runs after the page changes.
 */
export function useAutoFitOverflow(enabled, pathname) {
  useEffect(() => {
    if (!enabled) return undefined;
    const SCROLLERS = '.MuiDataGrid-root, .MuiTableContainer-root, table, svg, canvas, img, [role="grid"]';
    const insideScroller = (el, frame) => {
      for (let a = el.parentElement; a && a !== frame; a = a.parentElement) {
        const x = getComputedStyle(a).overflowX;
        if (x === 'auto' || x === 'scroll' || x === 'hidden' || x === 'clip') return true;
      }
      return false;
    };
    const run = () => {
      const frame = document.querySelector('[data-route]');
      if (!frame) return;
      const limit = frame.clientWidth;
      frame.querySelectorAll('*').forEach((el) => {
        if (el.dataset.rpFit || el.closest(SCROLLERS)) return;
        const cs = getComputedStyle(el);
        if (cs.display === 'none' || cs.position === 'fixed') return;
        // A box set to "overflow: hidden" around a single row of 3+ items that is wider than the box
        // (a tab strip) silently cuts the extra items off. Make it scroll sideways instead.
        if (cs.overflowX === 'hidden' && cs.textOverflow !== 'ellipsis' && el.children.length === 1
          && el.scrollWidth > el.clientWidth + 1) {
          const row = el.firstElementChild;
          const rcs = getComputedStyle(row);
          if (rcs.display.includes('flex') && rcs.flexDirection.startsWith('row') && rcs.flexWrap === 'nowrap'
            && row.children.length >= 3) {
            el.style.overflowX = 'auto';
            el.dataset.rpFit = 'scroll';
            return;
          }
        }
        if (cs.display.includes('flex') && cs.flexDirection.startsWith('row') && cs.flexWrap === 'nowrap'
          && el.scrollWidth > el.clientWidth + 1 && !insideScroller(el, frame)) {
          el.style.flexWrap = 'wrap';
          el.style.rowGap = '8px';
          el.dataset.rpFit = 'wrap';
          return;
        }
        if (el.getBoundingClientRect().width > limit + 1 && !insideScroller(el, frame)) {
          el.style.maxWidth = '100%';
          el.dataset.rpFit = 'cap';
        }
      });
    };
    let timer;
    const schedule = () => { clearTimeout(timer); timer = setTimeout(run, 300); };
    const root = document.getElementById('root');
    const observer = new MutationObserver(schedule);
    if (root) observer.observe(root, { childList: true, subtree: true });
    window.addEventListener('resize', schedule);
    schedule();
    return () => {
      clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [enabled, pathname]);
}

/** Narrowest a data-grid column may be on a phone; the table scrolls sideways instead of squeezing below it. */
export const PHONE_GRID_MIN_COLUMN_WIDTH = 130;

/**
 * Data-grid columns for the current screen. On a phone every column that sizes itself (flex) gets a
 * minimum width, so a table with many columns scrolls sideways instead of crushing them to ~50px
 * with cut-off headers. A column may set `phoneMinWidth` when its cell needs more than the default.
 * Fixed-width columns and every larger screen are returned unchanged.
 */
export function usePhoneGridColumns(columns) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down(PHONE_BREAKPOINT));
  return useMemo(() => {
    if (!isPhone || !Array.isArray(columns)) return columns;
    return columns.map((col) => (col.flex || col.width == null)
      ? { ...col, minWidth: Math.max(col.minWidth || 0, col.phoneMinWidth || PHONE_GRID_MIN_COLUMN_WIDTH) }
      : col);
  }, [isPhone, columns]);
}

/** Opens and closes the phone "Filters" bars (see phoneFilterPanelRules): a tap on the bar toggles it. */
export function usePhoneFilterToggle(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const isPhone = () => window.matchMedia(`(max-width: ${599.95}px)`).matches;
    const onClick = (event) => {
      if (!isPhone()) return;
      const panel = event.target?.closest?.('[data-rp-filters]');
      // Only a tap on the bar itself (the panel's own top strip), not on a field inside it.
      if (!panel || event.target !== panel || event.clientY - panel.getBoundingClientRect().top > 48) return;
      panel.dataset.open = panel.dataset.open === 'true' ? 'false' : 'true';
    };
    const onSubmit = (event) => {
      if (!isPhone()) return;
      const panel = event.target?.closest?.('[data-rp-filters]');
      if (panel) panel.dataset.open = 'false';
    };
    document.addEventListener('click', onClick);
    document.addEventListener('submit', onSubmit, true);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('submit', onSubmit, true);
    };
  }, [enabled]);
}

/** Phone-only fixes for shared components. Merged into the theme next to the other overrides. */
export function responsiveOverrides(theme) {
  const phone = theme.breakpoints.down(PHONE_BREAKPOINT);

  return {
    // Dialogs keep their size on desktop; on a phone they use the screen instead of a 64px margin.
    MuiDialog: {
      styleOverrides: {
        paper: {
          [phone]: {
            margin: 8,
            width: 'calc(100% - 16px)',
            maxWidth: 'calc(100% - 16px)',
            maxHeight: 'calc(100% - 16px)',
          },
        },
      },
    },

    // A tab bar wider than the screen scrolls sideways instead of cutting tabs off.
    MuiTabs: {
      styleOverrides: {
        scroller: {
          [phone]: {
            '&.MuiTabs-fixed': { overflowX: 'auto !important', scrollbarWidth: 'none' },
            '&.MuiTabs-fixed::-webkit-scrollbar': { display: 'none' },
          },
        },
        flexContainer: {
          [phone]: { '& .MuiTab-root': { flexShrink: 0 } },
        },
      },
    },

    // Menus and popups never run off the edge of the screen.
    MuiPopover: {
      styleOverrides: {
        paper: { [phone]: { maxWidth: 'calc(100vw - 16px)' } },
      },
    },
  };
}
